/**
 * Типы каталога «Анализ помещения» + generic-пересчёт рейтинга.
 *
 * Данные (критерии/веса/опции/пороги) НЕ хранятся здесь — они приходят с бэка
 * (GET /wiki/opening/room-criteria, единый источник истины). Здесь только тип и
 * чистый алгоритм пересчёта, общий для wiki-формы и CRM-редактора.
 */

export interface RoomOption {
  label: string;
  points: number;
}
export interface RoomCriterion {
  key: string;
  label: string;
  weight: number;
  hint?: string;
  options: RoomOption[];
}
export interface RoomTechItem {
  key: string;
  label: string;
  unit?: string;
  min?: number;
  max?: number;
}
export interface RoomTechBool {
  key: string;
  label: string;
}
export interface RoomPowerRule {
  techKey: string;
  triggerKey: string;
  triggerValues: string[];
  relaxedMin: number;
  relaxedLabel: string;
  strictLabel: string;
}
export interface RoomVerdictBand {
  minPercent: number;
  label: string;
}
export interface RoomCatalog {
  commercial: RoomCriterion[];
  investment: RoomCriterion[];
  tech: RoomTechItem[];
  techBool: RoomTechBool[];
  powerRule: RoomPowerRule;
  verdictBands: RoomVerdictBand[];
  stopVerdict: string;
  commMax: number;
}

export interface RoomAnswerLite {
  key: string;
  label: string;
  value: string;
  points: number;
  isCritical?: boolean;
}
export interface RoomComputed {
  commercial: RoomAnswerLite[];
  investment: RoomAnswerLite[];
  tech: RoomAnswerLite[];
  commercialPercent: number;
  investmentScore: number;
  techFails: string[];
  verdict: string;
}

export function parseNum(v?: string): number | null {
  if (!v) return null;
  const n = parseFloat(v.replace(',', '.').replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Динамический порог по power-rule: смягчён, если выбран один из triggerValues. */
export function powerRelaxed(cat: RoomCatalog, sel: Record<string, string>): boolean {
  return cat.powerRule.triggerValues.includes(sel[cat.powerRule.triggerKey]);
}
export function techMin(cat: RoomCatalog, sel: Record<string, string>, t: RoomTechItem): number | undefined {
  return t.key === cat.powerRule.techKey ? (powerRelaxed(cat, sel) ? cat.powerRule.relaxedMin : t.min) : t.min;
}
export function techLabel(cat: RoomCatalog, sel: Record<string, string>, t: RoomTechItem): string {
  if (t.key !== cat.powerRule.techKey) return t.label;
  return powerRelaxed(cat, sel) ? cat.powerRule.relaxedLabel : cat.powerRule.strictLabel;
}

/** Пересчёт рейтинга по карте «ключ → значение» (как `sel`). */
export function recomputeRoom(cat: RoomCatalog, sel: Record<string, string>): RoomComputed {
  const buildAnswers = (list: RoomCriterion[]): RoomAnswerLite[] =>
    list
      .filter((c) => sel[c.key])
      .map((c) => {
        const opt = c.options.find((o) => o.label === sel[c.key]);
        return { key: c.key, label: c.label, value: sel[c.key], points: opt ? opt.points : 0 };
      });

  const commercial = buildAnswers(cat.commercial);
  const investment = buildAnswers(cat.investment);
  const commScore = commercial.reduce((s, a) => s + a.points, 0);
  const invScore = investment.reduce((s, a) => s + a.points, 0);
  const commPct = cat.commMax > 0 ? Math.round((commScore / cat.commMax) * 100) : 0;

  const techFails: string[] = [];
  const tech: RoomAnswerLite[] = [];
  for (const t of cat.tech) {
    if (!sel[t.key]) continue;
    const n = parseNum(sel[t.key]);
    const min = techMin(cat, sel, t);
    const bad = n != null && ((min != null && n < min) || (t.max != null && n > t.max));
    const label = techLabel(cat, sel, t);
    if (bad) techFails.push(label);
    tech.push({ key: t.key, label, value: sel[t.key], points: 0, isCritical: bad });
  }
  for (const t of cat.techBool) {
    if (!sel[t.key]) continue;
    const isNo = sel[t.key] === 'no';
    if (isNo) techFails.push(t.label);
    tech.push({ key: t.key, label: t.label, value: isNo ? 'Нет' : 'Да', points: 0, isCritical: isNo });
  }

  let verdict = cat.stopVerdict;
  if (!techFails.length) {
    const band = cat.verdictBands.find((b) => commPct >= b.minPercent) ?? cat.verdictBands[cat.verdictBands.length - 1];
    verdict = band ? `${band.label} · ${commPct}%` : `${commPct}%`;
  }

  return {
    commercial,
    investment,
    tech,
    commercialPercent: commPct,
    investmentScore: Math.round(invScore * 10) / 10,
    techFails,
    verdict,
  };
}
